import native from '@/utils/native';
import { getNacosConfig } from '@/service/api';
import { getUrlParam, parseTime, throttleImmediate } from '@/utils/tool';

// 创建用户行为类
class OperateLog {
    // max 控制上报用户行为的最大条数
    max = 5;
    // stack 存储用户行为
    stack = [];
    // sls 配置信息
    slsConfig = {
        // 忽略的错误码
        ignoreErrno: []
    };
    // 添加用户行为栈
    push(data = {}) {
        if (this.stack.length >= this.max) {
            // 超出则删除第一条
            this.stack.shift();
        }
        this.stack.push({ time: parseTime(new Date()), URL: window.location.href, ...data });
    }
    // 获取日志
    getStack() {
        return this.stack;
    }
    // 设置 sls 配置
    setSlsConfig(data = {}) {
        this.slsConfig = data;
    }
    // 获取 sls 配置
    getSlsConfig() {
        return this.slsConfig;
    }
}

const operateLogSLS = new OperateLog();

const getSlsConfig = async () => {
    try {
        const res = await getNacosConfig('sls-config');
        const slsConfig = JSON.parse(res.data.content);
        operateLogSLS.setSlsConfig(slsConfig);
    } catch (err) {
        console.log(err);
    }
};

const send = throttleImmediate((data = {}) => {
    if (!operateLogSLS?.getSlsConfig()?.isUse) return;

    const _data = {
        time: parseTime(new Date()),
        versions: window.navigator.appVersion,
        userID: getUrlParam('userID') || '无userID',
        URL: window.location.host + window.location.pathname,
        FullURL: window.location.href,
        operateLog: operateLogSLS.getStack(),
        ...data
    };
    native.logRecord({ level: 4, key: 'content', JSONMsg: JSON.stringify(_data) });
}, 1000);

const initSLS = async (app) => {
    await getSlsConfig();

    if (!operateLogSLS?.getSlsConfig()?.isUse) return;

    window.addEventListener(
        'error',
        (event) => {
            let error = '';
            let errorType = '';
            const target = event.target;
            const targetStr = target?.outerHTML || '';
            if (target instanceof HTMLImageElement) {
                // 图片加载错误
                error = `Failed to load image: ${target.src},targetStr: ${targetStr}`;
                errorType = 'ImageError';
            } else if (target instanceof HTMLScriptElement) {
                // JS 加载错误（严重）
                error = `Failed to load script: ${target.src},targetStr: ${targetStr}`;
                errorType = 'ScriptError';
            } else if (target instanceof HTMLLinkElement) {
                // CSS 加载错误
                error = `Failed to load stylesheet: ${target.href},targetStr: ${targetStr}`;
                errorType = 'StylesheetError';
            } else {
                error = `General error: ${event.message} at ${event.filename}:${event.lineno}:${event.colno},targetStr: ${targetStr}`;
                errorType = 'GeneralError';
            }
            send({ error, errorType });
            console.error(event);
        },
        true
    );

    window.addEventListener('unhandledrejection', (event) => {
        const error = `Unhandled promise rejection: ${JSON.stringify(event.reason)}`;
        const errorType = 'PromiseError';
        send({ error, errorType });
        console.error(event);
    });

    app.config.errorHandler = (err, vm, info) => {
        const error = `Error in Vue instance: ${err.toString()}\nInfo: ${info}`;
        const errorType = 'VueError';
        send({ error, errorType });
        console.error(err, vm, info);
    };

    document.addEventListener(
        'click',
        (e) => {
            const { target = '', x = '', y = '' } = e;
            const tagName = target.tagName.toLowerCase();
            if (tagName === 'body') {
                return null;
            }
            let classNames = target.classList.value;
            classNames = classNames !== '' ? ` class="${classNames}"` : '';
            const id = target.id ? ` id="${target.id}"` : '';
            const innerText = target.innerText;
            const dom = `<${tagName}${id}${
                classNames !== '' ? classNames : ''
            } x=${x} y=${y}>${innerText}</${tagName}>`;
            operateLogSLS.push({ type: 'click', log: dom });
        },
        true
    );
};

// 记录正常请求的日志
const requestLogSLS = (response) => {
    if (!operateLogSLS?.getSlsConfig()?.isUse) return;

    let payload = {};
    if (response?.config?.params) {
        payload = response.config.params;
    }
    if (response?.config?.data) {
        payload = response.config.data;
    }

    operateLogSLS.push({
        type: 'request',
        log: {
            url: response.config.url,
            method: response.config.method,
            payload,
            preview: {
                errmsg: response.data.errmsg,
                errno: response.data.errno,
                traceId: response.data.traceId,
                data: response?.data?.data || ''
            }
        }
    });
};

// 接口请求成功，返回错误状态的日志上传
const sendRequestLogSLS = (response) => {
    if (!operateLogSLS?.getSlsConfig()?.isUse) return;

    const errno = response?.data?.errno || 0;

    // 不需要走错误状态日志上报，进入日志记录
    if (operateLogSLS?.getSlsConfig()?.ignoreErrno?.includes(errno)) {
        requestLogSLS(response);
        return;
    }

    let errorType = '';
    let axiosError = {};

    // 有错误码，业务错误信息
    if (errno) {
        errorType = 'Errno';
    }

    // 网络错误
    if (response?.error && response?.error?.name && response?.error?.name === 'AxiosError') {
        errorType = 'NetworkError';
        if (response?.error) {
            axiosError = { ...response?.error };
        }
    }

    let payload = {};
    if (response?.config?.params) {
        payload = response.config.params;
    }
    if (response?.config?.data) {
        payload = response.config.data;
    }

    send({
        error: {
            type: 'request',
            url: response?.config?.url || '',
            method: response?.config?.method || '',
            payload,
            preview: {
                errmsg: response?.data?.errmsg || '',
                errno,
                traceId: response?.data?.traceId || '',
                data: response?.data?.data || ''
            },
            ...axiosError
        },
        errorType
    });
};

export { initSLS, requestLogSLS, sendRequestLogSLS, send };
